// Manager editor for an agent's work days + leave dates (Team Users page).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];

export const TeamScheduleDialog = ({ user, onClose, headers }) => {
  const [sched, setSched] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setSched(null);
    axios.get(`${API}/domain/users/${user.id}/schedule`, { headers })
      .then((r) => setSched(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || 'Could not load schedule'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const days = sched?.work_days?.length ? sched.work_days : DAYS.map(([d]) => d);
  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d];
    if (!next.length) { toast.error('Keep at least one work day'); return; }
    setSched((p) => ({ ...p, work_days: next }));
  };
  const addDate = () => {
    if (!newDate) return;
    setSched((p) => ({ ...p, personal_holidays: [...new Set([...(p.personal_holidays || []), newDate])].sort() }));
    setNewDate('');
  };
  const removeDate = (d) => setSched((p) => ({ ...p, personal_holidays: (p.personal_holidays || []).filter((x) => x !== d) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/domain/users/${user.id}/schedule`,
        { work_days: days, personal_holidays: sched.personal_holidays || [] }, { headers });
      toast.success(`Schedule saved for ${sched?.name || user.name || user.email}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="team-schedule-dialog">
        <DialogHeader>
          <DialogTitle>Work days &amp; leave — {sched?.name || user.name || user.email}</DialogTitle>
          <DialogDescription className="text-xs">
            No morning email or roster expectations on days off. Leave dates also show on the Team leave banner and Supervisor Today.
          </DialogDescription>
        </DialogHeader>
        {!sched ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase text-slate-500 mb-1.5">Work days</p>
              <div className="flex gap-1.5 flex-wrap">
                {DAYS.map(([d, label]) => (
                  <button key={d} onClick={() => toggleDay(d)}
                    className={`text-xs font-bold px-2.5 py-1.5 rounded-full border ${days.includes(d)
                      ? 'bg-teal-600 border-teal-600 text-white'
                      : 'border-slate-300 text-slate-400 hover:border-slate-400'}`}
                    data-testid={`team-schedule-day-${d}`}>{label}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase text-slate-500 mb-1.5">Leave dates</p>
              <div className="flex items-center gap-2 mb-2">
                <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                  className="text-xs border border-slate-300 rounded-md px-2 py-1.5"
                  data-testid="team-schedule-date-input" />
                <button onClick={addDate} disabled={!newDate}
                  className="text-xs font-bold px-3 py-1.5 rounded-full bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-40"
                  data-testid="team-schedule-date-add">Add</button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(sched.personal_holidays || []).map((d) => (
                  <span key={d} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-100 rounded-full px-2 py-0.5">
                    {d}
                    <button onClick={() => removeDate(d)} className="opacity-50 hover:opacity-100 font-bold"
                      data-testid={`team-schedule-date-remove-${d}`}>×</button>
                  </span>
                ))}
                {!(sched.personal_holidays || []).length && <span className="text-[11px] text-slate-400 italic">No leave booked.</span>}
              </div>
            </div>
            <button onClick={save} disabled={saving}
              className="w-full py-2 rounded-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold disabled:opacity-50"
              data-testid="team-schedule-save-btn">
              {saving ? 'Saving…' : 'Save schedule'}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
